import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import {
  Loader2, FileText, CreditCard, ClipboardCheck, Users, MapPin,
  Phone, Mail, ChevronRight, Download, ExternalLink, Copy, Share2,
  CheckCircle, Clock, AlertCircle, DollarSign, Eye, Calendar, Star
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

function StatusBadge({ status }) {
  const map = {
    draft: { color: "bg-slate-100 text-slate-600", label: "Draft" },
    sent: { color: "bg-blue-100 text-blue-700", label: "Sent" },
    viewed: { color: "bg-amber-100 text-amber-700", label: "Viewed" },
    approved: { color: "bg-emerald-100 text-emerald-700", label: "Approved" },
    signed: { color: "bg-green-100 text-green-700", label: "Signed" },
    pending: { color: "bg-amber-100 text-amber-700", label: "Pending" },
    paid: { color: "bg-green-100 text-green-700", label: "Paid" },
    partial: { color: "bg-orange-100 text-orange-700", label: "Partial" },
    overdue: { color: "bg-red-100 text-red-700", label: "Overdue" },
    completed: { color: "bg-green-100 text-green-700", label: "Completed" },
    converted: { color: "bg-blue-100 text-blue-700", label: "Converted" },
    paid_out: { color: "bg-green-100 text-green-700", label: "Paid Out" },
  };
  const cfg = map[status] || { color: "bg-slate-100 text-slate-600", label: status };
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return dateStr; }
}

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount || 0);
}

export default function CustomerPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(null); // set after data loads
  const [showQR, setShowQR] = useState(true);

  // Show "Tides Team — John Smith" in iMessage previews
  const portalCompany = data?.company?.name || "Tides Team Exteriors";
  const portalContact = data?.contact?.name;
  useDocumentTitle(portalContact ? `${portalCompany} — ${portalContact}` : `${portalCompany} Customer Portal`);

  useEffect(() => {
    let cancelled = false;
    // Initial load — sets default activeTab. Subsequent refreshes preserve
    // whichever tab the customer is currently viewing.
    const initialLoad = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/portal/${token}`);
        if (cancelled) return;
        setData(res.data);
        const hasProjects = res.data.projects?.length > 0;
        setActiveTab(hasProjects ? "overview" : "referrals");
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? "Portal link is invalid or expired." : "Failed to load portal.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // Silent background refresh — doesn't change activeTab or trigger spinner.
    const silentRefresh = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/portal/${token}`);
        if (!cancelled) setData(res.data);
      } catch { /* ignore — keep last good data */ }
    };
    initialLoad();
    // Poll every 30s so billing/payments update without manual refresh.
    const interval = setInterval(silentRefresh, 30000);
    // Also refetch the moment the tab becomes visible again (e.g. customer
    // pays an invoice in another tab and comes back).
    const onVisible = () => { if (document.visibilityState === "visible") silentRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
    </div>
  );

  if (error || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-slate-800 mb-2">Portal Not Found</h2>
          <p className="text-sm text-slate-500">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  const { contact, company, projects, quotes, invoices, payments, inspections, referrals, activities } = data;
  const referralUrl = `${BASE_URL}/r/${contact.referral_code}`;

  const totalOwed = invoices.filter(i => i.status !== "paid").reduce((s, i) => s + (i.amount - (i.paid_amount || 0) - (i.discount || 0)), 0);
  const totalPaid = payments.reduce((s, p) => s + (p.amount || 0), 0);
  const hasProjects = projects.length > 0;

  const tabs = [
    ...(hasProjects ? [{ id: "overview", label: "Overview", icon: Eye }] : []),
    ...(hasProjects ? [{ id: "quotes", label: "Quotes", icon: FileText, count: quotes.length }] : []),
    ...(hasProjects ? [{ id: "billing", label: "Billing", icon: CreditCard, count: invoices.length }] : []),
    ...(hasProjects ? [{ id: "inspections", label: "Inspections", icon: ClipboardCheck, count: inspections.length }] : []),
    { id: "referrals", label: "Referrals", icon: Users, count: referrals.length },
  ];

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralUrl);
    toast.success("Referral link copied!");
  };

  const shareReferralLink = async () => {
    const text = `Hey! ${company?.name || "Tides Team"} just did some great work for me. If you ever need exterior work done, here's my referral link — they're top notch.`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${company?.name || "Tides Team"} — Referral`,
          text,
          url: referralUrl,
        });
        toast.success("Thanks for sharing!");
      } catch { /* user cancelled */ }
    } else {
      navigator.clipboard.writeText(`${text}\n${referralUrl}`);
      toast.success("Referral message copied — paste it anywhere!");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="customer-portal">
      {/* Header */}
      <div className="bg-[#0F4C81] text-white" data-testid="portal-header">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:px-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <img
                src="/logo-dark.png"
                alt={company.name}
                className="h-12 sm:h-14 w-auto rounded-md bg-[#0a3a66] p-1.5"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold tracking-tight">{company.name}</h1>
                <p className="text-blue-200 text-sm mt-0.5">Customer Portal</p>
              </div>
            </div>
            <div className="flex items-center gap-4 text-sm text-blue-200">
              {company.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{company.phone}</span>}
              {company.email && <span className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />{company.email}</span>}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-blue-400/30">
            <p className="text-sm text-blue-100">Welcome back, <strong className="text-white">{contact.name}</strong>
              {!hasProjects && <span className="ml-2 text-blue-300">&middot; Referral Partner</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Tab Nav */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-2 sm:px-6">
          <nav className="flex gap-0.5 sm:gap-1 -mb-px" data-testid="portal-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`portal-tab-${tab.id}`}
                className={`flex-1 min-w-0 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2 px-1 sm:px-4 py-2 sm:py-3 text-[11px] sm:text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? "border-[#0F4C81] text-[#0F4C81]"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                }`}
              >
                <tab.icon className="w-5 h-5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="truncate max-w-full leading-tight">{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full leading-none ${
                    activeTab === tab.id ? "bg-[#0F4C81]/10 text-[#0F4C81]" : "bg-slate-100 text-slate-500"
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {activeTab === "overview" && <OverviewTab data={data} totalOwed={totalOwed} totalPaid={totalPaid} referralUrl={referralUrl} copyReferralLink={copyReferralLink} shareReferralLink={shareReferralLink} showQR={showQR} setShowQR={setShowQR} company={company} />}
        {activeTab === "quotes" && <QuotesTab quotes={quotes} projects={projects} />}
        {activeTab === "billing" && <BillingTab invoices={invoices} payments={payments} projects={projects} />}
        {activeTab === "inspections" && <InspectionsTab inspections={inspections} />}
        {activeTab === "referrals" && <ReferralsTab referrals={referrals} referralUrl={referralUrl} referralCode={contact.referral_code} copyReferralLink={copyReferralLink} shareReferralLink={shareReferralLink} showQR={showQR} setShowQR={setShowQR} company={company} />}
      </div>
    </div>
  );
}


// ===================== OVERVIEW TAB =====================

function OverviewTab({ data, totalOwed, totalPaid, referralUrl, copyReferralLink, shareReferralLink, showQR, setShowQR, company }) {
  const { projects, quotes, invoices, activities, referrals } = data;
  const quoteBeingBuilt = projects.find(p => (p.stage || "").endsWith("_quote_being_built"));

  return (
    <div className="space-y-6" data-testid="portal-overview">
      {/* Quote-being-built banner — reassures the customer we're actively
          drafting when their SMS/email says so but no quote has arrived yet. */}
      {quoteBeingBuilt && (
        <div
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
          data-testid="portal-quote-building-banner"
        >
          <Clock className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900">Your quote is being prepared</p>
            <p className="text-xs text-amber-800 mt-0.5">We're drafting it right now and you'll receive it shortly. If anything comes to mind you'd like included, just reply to our text or email.</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard icon={MapPin} label="Projects" value={projects.length} color="blue" />
        <SummaryCard icon={FileText} label="Quotes" value={quotes.length} color="indigo" />
        <SummaryCard icon={DollarSign} label="Total Paid" value={formatCurrency(totalPaid)} color="green" />
        <SummaryCard icon={CreditCard} label="Balance Due" value={formatCurrency(totalOwed)} color={totalOwed > 0 ? "amber" : "green"} />
      </div>

      {/* Projects */}
      {projects.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-800">My Projects</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {projects.map(p => (
                <div key={p.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors" data-testid={`project-row-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800 truncate">{p.address || "No address"}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{p.service_type} &middot; Started {formatDate(p.created_at)}</p>
                  </div>
                  <Badge style={{ backgroundColor: p.stage_color + "20", color: p.stage_color, borderColor: p.stage_color + "40" }} className="text-xs border ml-3 whitespace-nowrap">{p.stage_name}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Referral CTA */}
      <Card className="border-[#0F4C81]/20 bg-gradient-to-br from-[#0F4C81]/5 via-transparent to-[#00A3E0]/5">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0F4C81]/10 flex items-center justify-center">
              <Share2 className="w-5 h-5 text-[#0F4C81]" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-800 text-sm">Refer a Friend, Earn Rewards</h3>
              <p className="text-xs text-slate-500 mt-0.5">Share your personal link — any work they book earns you a thank-you.</p>
              {data.referrals?.length > 0 && (
                <p className="text-xs text-[#0F4C81] font-semibold mt-1">{data.referrals.length} referral{data.referrals.length !== 1 ? "s" : ""} sent</p>
              )}
            </div>
          </div>
          {/* Inline link display */}
          <div className="flex items-center gap-2 bg-white rounded-md border border-slate-200 px-3 py-2">
            <Share2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
            <div className="flex-1 truncate text-xs font-mono text-slate-600" data-testid="portal-referral-url">{referralUrl}</div>
            <button
              type="button"
              onClick={copyReferralLink}
              className="text-[#0F4C81] hover:text-[#0d3f6a] p-1 flex-shrink-0"
              title="Copy link"
              data-testid="portal-copy-referral"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={shareReferralLink} className="flex-1 bg-[#0F4C81] hover:bg-[#0d3f6a] text-xs h-9" data-testid="portal-share-referral">
              <Share2 className="w-3.5 h-3.5 mr-1.5" />Share
            </Button>
            <Button size="sm" variant="outline" onClick={() => setShowQR(!showQR)} className="text-xs h-9" data-testid="portal-qr-toggle">
              {showQR ? "Hide" : "Show"} QR
            </Button>
          </div>
          {showQR && (
            <div className="flex justify-center pt-2">
              <div className="bg-white p-4 rounded-lg shadow-sm border" data-testid="portal-qr-code">
                <QRCodeSVG value={referralUrl} size={160} level="M" />
                <p className="text-xs text-slate-400 text-center mt-2">Scan to refer</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave a Review (Google + Facebook side-by-side) */}
      <ReviewCardsRow company={company} />

      {/* Recent Activity */}
      {activities.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-semibold text-slate-800">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100 max-h-[300px] overflow-y-auto">
              {activities.slice(0, 10).map((a, i) => (
                <div key={i} className="flex items-start gap-3 px-5 py-3">
                  <div className="mt-0.5"><Clock className="w-3.5 h-3.5 text-slate-400" /></div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-700">{a.details}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{formatDate(a.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, color }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    indigo: "bg-indigo-50 text-indigo-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
  };
  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-2 ${colors[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold text-slate-800 mt-0.5">{value}</p>
      </CardContent>
    </Card>
  );
}


// ===================== QUOTES TAB =====================

function QuotesTab({ quotes, projects }) {
  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p; });

  if (!quotes.length) return <EmptyState icon={FileText} title="No Quotes Yet" desc="You don't have any quotes yet." />;

  return (
    <div className="space-y-3" data-testid="portal-quotes-tab">
      {quotes.map(q => {
        const proj = projectMap[q.project_id];
        return (
          <Card key={q.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800">Quote #{q.quote_number}</span>
                    <StatusBadge status={q.status} />
                  </div>
                  {proj && <p className="text-xs text-slate-500">{proj.address} &middot; {proj.service_type}</p>}
                  <p className="text-xs text-slate-400 mt-0.5">Created {formatDate(q.created_at)}{q.valid_until ? <> &middot; Valid until {formatDate(q.valid_until)}</> : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-slate-800">{formatCurrency(q.total)}</span>
                  <a href={`${BASE_URL}/public/quote/${q.id}`} target="_blank" rel="noopener noreferrer" data-testid={`view-quote-${q.id}`}>
                    <Button size="sm" variant="outline" className="text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" />View</Button>
                  </a>
                </div>
              </div>
              {/* Signature status */}
              <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs">
                <span className={`flex items-center gap-1 ${q.customer_signature ? "text-green-600" : "text-slate-400"}`}>
                  {q.customer_signature ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  Your Signature
                </span>
                <span className={`flex items-center gap-1 ${q.admin_signature ? "text-green-600" : "text-slate-400"}`}>
                  {q.admin_signature ? <CheckCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                  Company Signature
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


// ===================== BILLING TAB =====================

function BillingTab({ invoices, payments, projects }) {
  const projectMap = {};
  projects.forEach(p => { projectMap[p.id] = p; });

  return (
    <div className="space-y-6" data-testid="portal-billing-tab">
      {/* Invoices */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Invoices</h3>
        {!invoices.length ? (
          <EmptyState icon={CreditCard} title="No Invoices" desc="No invoices have been issued yet." small />
        ) : (
          <div className="space-y-3">
            {invoices.map(inv => {
              const remaining = Math.max(0, (inv.amount || 0) - (inv.paid_amount || 0) - (inv.discount || 0));
              const proj = projectMap[inv.project_id];
              return (
                <Card key={inv.id}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-semibold text-slate-800">{inv.invoice_number}</span>
                          <StatusBadge status={inv.status} />
                        </div>
                        <p className="text-xs text-slate-500">{inv.description || "Invoice"}</p>
                        {proj && <p className="text-xs text-slate-400 mt-0.5">{proj.address}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-slate-800">{formatCurrency(inv.amount)}</p>
                        {inv.status !== "paid" && remaining > 0 && (
                          <p className="text-xs text-amber-600 font-medium">Balance: {formatCurrency(remaining)}</p>
                        )}
                        {inv.status !== "paid" && remaining > 0 && (
                          <a href={`${BASE_URL}/pay/${inv.id}`} target="_blank" rel="noopener noreferrer" data-testid={`pay-invoice-${inv.id}`}>
                            <Button size="sm" className="mt-2 text-xs bg-[#0F4C81] hover:bg-[#0d3f6a]">
                              <CreditCard className="w-3.5 h-3.5 mr-1" />Pay Now
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Payment History */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Payment History</h3>
        {!payments.length ? (
          <EmptyState icon={DollarSign} title="No Payments" desc="No payments recorded yet." small />
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y divide-slate-100">
                {payments.map(pay => (
                  <div key={pay.id} className="flex items-center justify-between px-5 py-3.5">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{formatCurrency(pay.amount)}</p>
                      <p className="text-xs text-slate-500 capitalize">{pay.method} &middot; {formatDate(pay.date)}</p>
                    </div>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}


// ===================== INSPECTIONS TAB =====================

function InspectionsTab({ inspections }) {
  if (!inspections.length) return <EmptyState icon={ClipboardCheck} title="No Inspections" desc="No inspection reports available." />;

  return (
    <div className="space-y-3" data-testid="portal-inspections-tab">
      {inspections.map(insp => (
        <Card key={insp.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-slate-800">Report #{insp.report_number}</span>
                  <StatusBadge status={insp.status} />
                </div>
                <p className="text-xs text-slate-500">{insp.property_address} &middot; {insp.report_type}</p>
                {insp.inspection_date && <p className="text-xs text-slate-400 mt-0.5">{formatDate(insp.inspection_date)}</p>}
                {insp.summary && <p className="text-xs text-slate-500 mt-1.5 line-clamp-2">{insp.summary}</p>}
              </div>
              <a href={`${BASE_URL}/public/inspection/${insp.id}`} target="_blank" rel="noopener noreferrer" data-testid={`view-inspection-${insp.id}`}>
                <Button size="sm" variant="outline" className="text-xs"><ExternalLink className="w-3.5 h-3.5 mr-1" />View Report</Button>
              </a>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}


// ===================== REFERRALS TAB =====================

function ReferralsTab({ referrals, referralUrl, referralCode, copyReferralLink, shareReferralLink, showQR, setShowQR, company }) {
  return (
    <div className="space-y-6" data-testid="portal-referrals-tab">
      {/* Referral Link Card */}
      <Card className="border-[#0F4C81]/20">
        <CardContent className="p-5 sm:p-6">
          <div className="text-center mb-5">
            <div className="w-12 h-12 bg-[#0F4C81]/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Share2 className="w-6 h-6 text-[#0F4C81]" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">Share & Earn</h3>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">Refer friends, family, or neighbors. When they become a customer, you earn a reward!</p>
          </div>

          {/* Referral Link */}
          <div className="bg-slate-50 rounded-lg p-3 flex items-center gap-2 mb-3" data-testid="referral-link-display">
            <div className="flex-1 truncate text-sm text-slate-600 font-mono">{referralUrl}</div>
            <Button size="sm" onClick={copyReferralLink} variant="outline" className="shrink-0 text-xs" data-testid="copy-referral-btn">
              <Copy className="w-3.5 h-3.5 mr-1" />Copy
            </Button>
          </div>

          {/* Share button (uses native iOS/Android share sheet when available) */}
          <Button size="sm" onClick={shareReferralLink} className="w-full bg-[#0F4C81] hover:bg-[#0d3f6a] mb-4" data-testid="share-referral-btn">
            <Share2 className="w-4 h-4 mr-1.5" />Share Referral Link
          </Button>

          {/* QR Code */}
          <div className="text-center">
            <Button variant="ghost" size="sm" onClick={() => setShowQR(!showQR)} className="text-xs text-[#0F4C81]" data-testid="toggle-qr-btn">
              {showQR ? "Hide QR Code" : "Show QR Code"}
            </Button>
            {showQR && (
              <div className="mt-4 inline-block bg-white p-5 rounded-xl shadow-sm border" data-testid="referral-qr-code">
                <QRCodeSVG value={referralUrl} size={200} level="H" includeMargin />
                <p className="text-xs text-slate-500 mt-3">Your referral code: <strong className="text-slate-800">{referralCode}</strong></p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Leave a Review (Google + Facebook side-by-side) */}
      <ReviewCardsRow company={company} />

      {/* Referral History */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Your Referrals ({referrals.length})</h3>
        {!referrals.length ? (
          <EmptyState icon={Users} title="No Referrals Yet" desc="Share your link to start earning rewards!" small />
        ) : (
          <div className="space-y-2">
            {referrals.map(ref => (
              <Card key={ref.id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{ref.referred_name}</p>
                    <p className="text-xs text-slate-400">{formatDate(ref.created_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {ref.payout_amount > 0 && <span className="text-sm font-semibold text-green-600">{formatCurrency(ref.payout_amount)}</span>}
                    <StatusBadge status={ref.status} />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ===================== REVIEW CARDS (Google + Facebook side-by-side) =====================

function ReviewPlatformCard({ url, label, brandColor, brandBg, icon, testId }) {
  if (!url) return null;
  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success(`${label} link copied!`);
  };
  return (
    <Card className="border-slate-200 flex-1 min-w-0" data-testid={`portal-${testId}-card`}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div
            className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
            style={{ backgroundColor: brandBg }}
          >
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-slate-800 text-sm leading-tight">Review on {label}</h4>
          </div>
          <button
            type="button"
            onClick={copyLink}
            className="text-slate-400 hover:text-slate-600 p-1 flex-shrink-0"
            title="Copy link"
            data-testid={`portal-copy-${testId}-url`}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer" className="block" data-testid={`portal-${testId}-cta`}>
          <Button
            size="sm"
            className="w-full text-white text-xs h-9"
            style={{ backgroundColor: brandColor }}
          >
            Leave a Review
          </Button>
        </a>
        <div className="flex justify-center">
          <div className="bg-white p-2 rounded-md border border-slate-200" data-testid={`portal-${testId}-qr`}>
            <QRCodeSVG value={url} size={110} level="M" />
          </div>
        </div>
        <p className="text-[10px] text-slate-400 text-center">Scan or tap to review</p>
      </CardContent>
    </Card>
  );
}

function ReviewCardsRow({ company }) {
  const g = company?.google_review_url;
  const f = company?.facebook_review_url;
  if (!g && !f) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50/40 p-4 space-y-3" data-testid="portal-review-cards">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
          <Star className="w-5 h-5 text-amber-500 fill-amber-500" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-slate-800 text-sm">Loved working with us? Leave a review!</h3>
          <p className="text-xs text-slate-500 mt-0.5">A quick review helps neighbors find us and means a lot to the team. Thank you!</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <ReviewPlatformCard
          url={g}
          label="Google"
          brandColor="#f59e0b"
          brandBg="#fef3c7"
          icon={<Star className="w-4 h-4 text-amber-500 fill-amber-500" />}
          testId="google-review"
        />
        <ReviewPlatformCard
          url={f}
          label="Facebook"
          brandColor="#1877F2"
          brandBg="#dbeafe"
          icon={<svg viewBox="0 0 24 24" className="w-4 h-4" fill="#1877F2" aria-hidden="true"><path d="M24 12.073C24 5.405 18.627 0 12 0 5.373 0 0 5.405 0 12.073c0 6.019 4.388 11.012 10.125 11.927v-8.437H7.078v-3.49h3.047V9.41c0-3.014 1.793-4.677 4.532-4.677 1.312 0 2.686.235 2.686.235v2.97h-1.514c-1.491 0-1.954.93-1.954 1.886v2.265h3.328l-.532 3.49h-2.796V24C19.612 23.085 24 18.092 24 12.073z"/></svg>}
          testId="facebook-review"
        />
      </div>
    </div>
  );
}


// ===================== EMPTY STATE =====================

function EmptyState({ icon: Icon, title, desc, small }) {
  return (
    <div className={`text-center ${small ? "py-8" : "py-16"}`}>
      <Icon className={`${small ? "w-8 h-8" : "w-12 h-12"} text-slate-300 mx-auto mb-3`} />
      <h3 className={`font-semibold text-slate-600 ${small ? "text-sm" : ""}`}>{title}</h3>
      <p className="text-xs text-slate-400 mt-1">{desc}</p>
    </div>
  );
}
